import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// MUI Components
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';

// MUI Icons
import PeopleIcon from '@mui/icons-material/People';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';

const Dashboard: React.FC = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const data = await getDashboardStats();
        setStats(data);
      } catch (error: any) {
        toast.error(error.message || 'Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardStats();
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} sx={{ color: '#7065F0' }} />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Dashboard
      </Typography>
      
      {/* Overview Cards */}
      <Grid container spacing={3} mb={5}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ backgroundColor: '#EDE9FF', color: '#7065F0', mr: 2 }}>
                <PeopleIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Users
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#0F0F2D', mb: 1 }}>
              {stats?.usersStats?.totalUsers || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Owners: {stats?.usersStats?.totalOwners || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Agents: {stats?.usersStats?.totalAgents || 0}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ backgroundColor: '#F0EDFF', color: '#7065F0', mr: 2 }}>
                <HomeWorkIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Properties
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#0F0F2D', mb: 1 }}>
              {stats?.propertiesStats?.totalProperties || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active: {stats?.propertiesStats?.activeProperties || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending: {stats?.propertiesStats?.pendingProperties || 0}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ backgroundColor: '#F0EDFF', color: '#7065F0', mr: 2 }}>
                <DirectionsWalkIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Tours
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#0F0F2D', mb: 1 }}>
              {stats?.toursStats?.totalTours || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending: {stats?.toursStats?.pendingTours || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed: {stats?.toursStats?.completedTours || 0}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ backgroundColor: '#F0EDFF', color: '#7065F0', mr: 2 }}>
                <ReceiptIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Rentals
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#0F0F2D', mb: 1 }}>
              {stats?.rentalsStats?.totalRentals || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Recent Activity Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
            <CardHeader 
              title="Recent Users" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600, color: '#0F0F2D' }}
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List>
                {stats?.usersStats?.recentUsers?.map((user: any, index: number) => (
                  <React.Fragment key={user._id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#7065F0' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{user.name}</Typography>}
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {user.email}
                            </Typography>
                            <br />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                display: 'inline-block',
                                backgroundColor: user.role === 'ADMIN' ? '#F0EDFF' : 
                                  user.role === 'AGENT' ? '#E1FCEF' : 
                                    user.role === 'OWNER' ? '#FFF4E5' : '#F0F9FF',
                                color: user.role === 'ADMIN' ? '#7065F0' : 
                                  user.role === 'AGENT' ? '#10B981' : 
                                    user.role === 'OWNER' ? '#FF8A00' : '#0EA5E9',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                mt: 0.5,
                              }}
                            >
                              {user.role}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < stats.usersStats.recentUsers.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
                {(!stats?.usersStats?.recentUsers || stats.usersStats.recentUsers.length === 0) && (
                  <ListItem>
                    <ListItemText primary="No recent users" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
            <CardHeader 
              title="Recent Properties" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600, color: '#0F0F2D' }}
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <List>
                {stats?.propertiesStats?.recentProperties?.map((property: any, index: number) => (
                  <React.Fragment key={property._id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#7065F0' }}>
                          <HomeIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{property.title}</Typography>}
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {property.address?.city}, {property.address?.state}
                            </Typography>
                            <br />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                display: 'inline-block',
                                backgroundColor: property.status === 'ACTIVE' ? '#E1FCEF' : 
                                  property.status === 'PENDING' ? '#FFF4E5' : '#FEEEF0',
                                color: property.status === 'ACTIVE' ? '#10B981' : 
                                  property.status === 'PENDING' ? '#FF8A00' : '#F43F5E',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                mt: 0.5,
                              }}
                            >
                              {property.status}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < stats.propertiesStats.recentProperties.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
                {(!stats?.propertiesStats?.recentProperties || stats.propertiesStats.recentProperties.length === 0) && (
                  <ListItem>
                    <ListItemText primary="No recent properties" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 